import React, { useState, useEffect } from 'react';
import {
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip
} from '@material-ui/core';
import { 
  PictureAsPdf,
  GetApp,
  Error as ErrorIcon,
  InsertDriveFile
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
  pdfContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5), // ✅ Padding reduzido
    margin: 0, // ✅ Sem margin - a bolha já tem
    marginBottom: theme.spacing(1.5), // ✅ Espaço inferior para o timestamp
    backgroundColor: 'transparent', // ✅ Transparente - sem card duplicado
    borderRadius: 0, // ✅ Sem borda - a bolha já tem
    border: 'none', // ✅ Sem borda
    maxWidth: '100%',
    minWidth: 0,
    cursor: 'pointer',
    position: 'relative', // ✅ Para z-index funcionar
    zIndex: 1, // ✅ Abaixo do timestamp
    transition: 'opacity 0.2s ease-in-out', // ✅ Apenas transição de opacidade
    '&:hover': {
      opacity: 0.8, // ✅ Apenas mudança de opacidade
    }
  },
  
  pdfIcon: {
    fontSize: 48,
    color: '#dc3545', // Cor vermelha do PDF
    marginRight: theme.spacing(2),
    flexShrink: 0,
  },
  
  documentIcon: {
    fontSize: 48,
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
    flexShrink: 0,
  },
  
  pdfContent: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    minWidth: 0,
    gap: theme.spacing(1.5),
  },

  pdfInfo: {
    flex: 1,
    minWidth: 0, // Para permitir text overflow
  },

  pdfHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    minWidth: 0,
    gap: theme.spacing(1),
  },

  pdfHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  
  pdfTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.3,
    marginBottom: theme.spacing(0.5),
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  
  pdfMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    flexWrap: 'wrap',
  },
  
  pdfSize: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  
  pdfType: {
    fontSize: '0.7rem',
  },
  
  downloadButton: {
    flexShrink: 0,
    alignSelf: 'flex-start',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    }
  },
  
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    color: theme.palette.error.main,
  },
  
  errorIcon: {
    marginRight: theme.spacing(1),
  },
  
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  }
}));

const PdfPreview = ({ 
  url, 
  filename = "documento.pdf", 
  size = null,
  onDownload = null,
  mediaType = null
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isPdfValid, setIsPdfValid] = useState(false);
  const [fileInfo, setFileInfo] = useState({
    name: filename,
    size: size,
    type: mediaType || 'document'
  });

  useEffect(() => {
    const loadFileInfo = async () => {
      try {
        setLoading(true);
        
        // Verificar se é realmente um PDF
        const isPdf = checkIfPdf(url, filename, mediaType);
        setIsPdfValid(isPdf);
        
        // Extrair nome do arquivo da URL se não fornecido adequadamente
        if (!filename || filename === "documento.pdf" || filename.length < 3) {
          try {
            const urlParts = url.split('/');
            const urlFilename = urlParts[urlParts.length - 1];
            const decodedFilename = decodeURIComponent(urlFilename);
            
            if (decodedFilename && decodedFilename.length > 3) {
              setFileInfo(prev => ({
                ...prev,
                name: decodedFilename
              }));
            }
          } catch (parseError) {
            console.warn('Erro ao extrair nome do arquivo:', parseError);
          }
        }
        
        setError(false);
      } catch (err) {
        console.error('Erro ao carregar informações do arquivo:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (url) {
      loadFileInfo();
    } else {
      setLoading(false);
      setError(true);
    }
  }, [url, filename, size, mediaType]);

  // Verificar se é PDF com base em múltiplos critérios
  const checkIfPdf = (url, filename, mediaType) => {
    // 1. Verificar pelo mediaType exato
    if (mediaType === "application/pdf") {
      return true;
    }

    // 2. Verificar pela extensão no filename
    if (filename && typeof filename === 'string') {
      const name = filename.toLowerCase();
      if (name.endsWith('.pdf')) {
        return true;
      }
    }

    // 3. Verificar pela URL
    if (url && typeof url === 'string') {
      const urlLower = url.toLowerCase();
      if (urlLower.endsWith('.pdf') || urlLower.includes('.pdf?')) {
        return true;
      }
    }

    return false;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(size < 10 ? 1 : 0)} ${units[unitIndex]}`;
  };

  const getFileExtension = (filename) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'DOC';
  };

  const handleDownload = (event) => {
    event.stopPropagation();
    
    if (onDownload) {
      onDownload(url, fileInfo.name);
    } else {
      // Download padrão
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileInfo.name;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Erro no download:', error);
        // Fallback: abrir em nova aba
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleContainerClick = () => {
    // Ao clicar no container, faz download diretamente
    handleDownload({ stopPropagation: () => {} });
  };

  if (loading) {
    return (
      <div className={classes.pdfContainer}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={20} />
          <Typography variant="body2" style={{ marginLeft: 8 }}>
            Carregando...
          </Typography>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.pdfContainer}>
        <div className={classes.errorContainer}>
          <ErrorIcon className={classes.errorIcon} />
          <div>
            <Typography variant="body2">
              {i18n.t("pdfPreview.loadError")}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {fileInfo.name}
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={classes.pdfContainer}
      onClick={handleContainerClick}
    >
      <div className={classes.pdfContent}>
        {isPdfValid ? (
          <PictureAsPdf className={classes.pdfIcon} />
        ) : (
          <InsertDriveFile className={classes.documentIcon} />
        )}

        <div className={classes.pdfInfo}>
          <div className={classes.pdfHeader}>
            <div className={classes.pdfHeaderText}>
              <Typography className={classes.pdfTitle} title={fileInfo.name}>
                {fileInfo.name}
              </Typography>

              <div className={classes.pdfMeta}>
                <Chip
                  label={isPdfValid ? 'PDF' : getFileExtension(fileInfo.name)}
                  size="small"
                  className={classes.pdfType}
                  color={isPdfValid ? 'secondary' : 'default'}
                />

                {fileInfo.size && (
                  <Typography variant="caption" className={classes.pdfSize}>
                    {formatFileSize(fileInfo.size)}
                  </Typography>
                )}
              </div>
            </div>

            <Tooltip title={i18n.t("pdfPreview.downloadFile")}>
              <IconButton
                className={classes.downloadButton}
                onClick={handleDownload}
                size="small"
              >
                <GetApp />
              </IconButton>
            </Tooltip>
          </div>

          <div className={classes.statusIndicator}>
            <Typography variant="caption" color="textSecondary">
              {i18n.t("pdfPreview.clickToDownload")}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfPreview;
